import { Box, Stack, Text } from '@mantine/core'

import type { PredictionContract, PredictionEvent } from '@/services/prediction/types'

import { CompactCardFooter } from '../_shared/CompactCardFooter'
import { CompactLiveLabel } from '../_shared/CompactLiveLabel'
import { CompetitorLogo } from '../_shared/CompetitorLogo'
import { LiveScoreBox } from '../_shared/LiveScoreBox'
import { MarketCardHeader } from '../_shared/MarketCardHeader'
import { PriceCell } from '../_shared/PriceCell'
import { VolumeLabel } from '../_shared/VolumeLabel'
import { WinChanceBar } from '../_shared/WinChanceBar'
import { HydrationSafe } from '../hooks/HydrationSafe'
import { useGetTeamColor } from '../hooks/useGetTeamColor'
import { useIsEventLive } from '../hooks/useIsEventLive'
import { useMatchData } from '../hooks/useMatchData'
import { useMatchSections } from '../hooks/useMatchSections'
import { useScheduleLabel } from '../hooks/useScheduleLabel'
import { getPersonInitials, getTitleInitials } from '../utils/abbreviatePersonName'
import { formatOddsMultiplier } from '../utils/formatOddsMultiplier'
import { getScoreForContract } from '../utils/liveScore'

const LOGO_SIZE = 40
const LOGO_RADIUS = 8

interface TwoWayCardProps {
  event: PredictionEvent
  /**
   * 页头图标（已解析的兜底链 `event.logo_url ?? 组图标`）。组图标不在事件
   * payload 上（BE 不下发），由 section 按 event_kind_group_id 从组列表查好传入。
   */
  iconSrc?: string
  /** 页头 kind 名兜底：section 已按 event.event_kind 反查组 display_name。 */
  kindName?: string
  onClick?: () => void
  /** 预留 stub：moneyline 价格格点击（本区暂无跳转/交易动作）。 */
  onContractClick?: (contract: PredictionContract) => void
}

/**
 * Port of cdc-web `Compact/TwoWayCard/index.tsx` — Streamline moneyline-only 卡片
 * （OG-9666 / OG-9944 / Figma 2466-105099 Default & 2466-105436 Live）。
 *
 * 差异（均已决策）：
 * - 整卡跳转改为同源外链 `/explore/predict/markets/{slug}`（当前标签页跳转），
 *   由 section 在 .cardRoot 上包绝对定位 overlay 链接实现；卡片内部仍无路由，
 *   `usePredictPathPrefix` / `getContractHref` 未移植。卡容器交互样式（hover
 *   抬升）由外层 section 的 CSS 提供，`.cardRoot` 类名由调用方传入段级样式表；
 * - 类型换成 cdc-market 的 `PredictionEvent` / `PredictionContract`；
 * - `useAdditionalMarketsCount`（detail 页口径）→ `useMatchSections.remainingCount`
 *   （总数 − 本卡已展示的近似值）；
 * - 页头图标兜底链 `event.logo_url ?? 组图标`（cdc-web `useEventKindGroupIcon`）
 *   改由调用方解析后经 `iconSrc` prop 传入（组图标走 event kind groups 查询）。
 *
 * 布局（纵向堆叠）：
 *   MarketCardHeader（图标盒 + kind 名 / 标题 / 时间或 LIVE 徽标）
 *   两行 OutcomeRow（logo + 名字 + WinChanceBar ‖ 倍数或比分 + moneyline %格）
 *   页脚（volume / parlay 标记 / "+N more markets"）
 *
 * 比分 vs 倍数在卡片级一次决定（`showScores`），两行永远同一模式：仅当 live 且
 * 至少一侧有真实比分时显示比分；比分模式下一侧缺分则该侧留空（LiveScoreBox 自
 * 隐藏），不回退倍数——保持两行对称。`'0'` / 0 是真实 0-0 比分，计入有分。
 */
export function TwoWayCard({
  event,
  iconSrc,
  kindName,
  onContractClick,
}: Readonly<TwoWayCardProps>) {
  const isLive = useIsEventLive(event)
  const sections = useMatchSections(event)
  const getTeamColor = useGetTeamColor(event)
  const { name, color, isPlayerMatch, playerImages } = useMatchData(event, getTeamColor)
  const { label: scheduleLabel, datetime: scheduleDatetime } = useScheduleLabel(event)

  const leftContract = sections.moneyline?.top ?? null
  const rightContract = sections.moneyline?.bottom ?? null

  // 无 logo 时 fallback badge 文本：真实 `team.abbrev` 优先（原样，如 "LAL"）；
  // 选手用 first+last name 首字母（"JL"）；队伍/国家用标题首字母（单词名取前
  // 两个字母，"USA" → "US"、"Europe" → "EU"），让国家读起来是代码而非未截断
  // 的单词（OG-9916）。直接用 level-1 名字会错误显示 "USA"/"Eur…"。
  const badgeText = [
    resolveBadgeText(name[0], leftContract),
    resolveBadgeText(name[1], rightContract),
  ]

  const subtitle = isLive ? (
    <CompactLiveLabel event={event} />
  ) : (
    <HydrationSafe>
      <Text
        variant="subtitle"
        c="var(--content-secondary)"
        component="time"
        dateTime={scheduleDatetime}
        suppressHydrationWarning
        truncate
      >
        {scheduleLabel}
      </Text>
    </HydrationSafe>
  )

  const liveScores = event.live_coverage?.scores
  const leftScore = getScoreForContract(leftContract ?? undefined, liveScores)?.score
  const rightScore = getScoreForContract(rightContract ?? undefined, liveScores)?.score

  const hasScore = (s?: string | number | null) => s !== undefined && s !== null && s !== ''
  const showScores = isLive && (hasScore(leftScore) || hasScore(rightScore))

  return (
    <Box data-redesigned-card="true" style={{ position: 'relative', height: '100%' }}>
      {/* minWidth: 0 —— 窄卡（窗缩小时）下缺省会按行 max-content（logo+全文
          label+倍数+%格 ≈ 349px）撑宽卡片，行文字溢出盖住 leverage（同
          FutureCard 修复）。 */}
      <Stack gap="sm" h="100%" style={{ minWidth: 0 }}>
        <MarketCardHeader
          iconSrc={iconSrc}
          kindName={event.event_kind_display_name || kindName}
          title={event.title}
          subtitle={subtitle}
        />

        <Stack gap="2xs" style={{ minWidth: 0 }}>
          <OutcomeRow
            name={name[0]}
            badgeText={badgeText[0]}
            primaryColor={color[0]?.primaryColor}
            logoUrl={isPlayerMatch ? undefined : color[0]?.logoUrl}
            playerImage={isPlayerMatch ? playerImages[0] : undefined}
            contract={leftContract}
            showScore={showScores}
            score={leftScore}
            onContractClick={onContractClick}
          />
          <OutcomeRow
            name={name[1]}
            badgeText={badgeText[1]}
            primaryColor={color[1]?.primaryColor}
            logoUrl={isPlayerMatch ? undefined : color[1]?.logoUrl}
            playerImage={isPlayerMatch ? playerImages[1] : undefined}
            contract={rightContract}
            showScore={showScores}
            score={rightScore}
            onContractClick={onContractClick}
          />
        </Stack>

        {/* 页脚包装的门控条件与 CompactCardFooter 自身可见性一致
            （moreCount > 0 || combos_enabled || volume）：footer 为空时返回
            null，但常驻包装会作为 `gap="sm"` Stack 的 flex 子项留下幽灵 16px
            间距。VolumeLabel 在无 `cumulative_traded_usd` 时自隐藏
            （OG-9908），不会出现孤儿 volume 标签。 */}
        {(sections.remainingCount > 0 || event.combos_enabled || event.cumulative_traded_usd) && (
          <Box data-card-footer style={{ marginTop: 'auto' }}>
            <CompactCardFooter
              moreCount={sections.remainingCount}
              combosEnabled={event.combos_enabled}
              volumeSlot={<VolumeLabel volume={event.cumulative_traded_usd} />}
            />
          </Box>
        )}
      </Stack>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Fallback badge text
// ---------------------------------------------------------------------------

/**
 * 单个结果的 fallback badge 文本。优先级与 SixBoxCard 一致：真实
 * `team.abbrev` 原样优先；选手退到 first+last name 首字母（"JL"）；队伍/国家
 * 退到标题首字母（单词名取前两个字母，"USA" → "US"），让国家读起来是代码
 * 而不是孤字母或未截断的单词（OG-9916）。
 */
function resolveBadgeText(fullName: string, contract: PredictionContract | null): string {
  if (contract?.team?.abbrev) return contract.team.abbrev
  const { first_name, last_name } = contract?.individual_profile ?? {}
  if (first_name && last_name) {
    return getPersonInitials(fullName, { firstName: first_name, lastName: last_name })
  }
  return getTitleInitials(fullName)
}

// ---------------------------------------------------------------------------
// Outcome row
// ---------------------------------------------------------------------------

interface OutcomeRowProps {
  name: string
  /** 无 logo 时的 fallback-badge 文本（队 abbrev / 选手首字母 / 标题首字母）。 */
  badgeText: string
  primaryColor?: string
  logoUrl?: string
  playerImage?: { src?: string; isFlag: boolean }
  contract: PredictionContract | null
  /** 卡片级比分模式（两行共享）：true → 显示比分盒（该侧无分则留空）。 */
  showScore: boolean
  score?: string
  onContractClick?: (contract: PredictionContract) => void
}

/**
 * 单个结果行：前导 40px logo + 名字（带比例胜率下划线），右侧簇放倍数
 * （非 live）或比分盒，最后是 moneyline % 价格格。对齐 og-app
 * `renderPercentageTeamRow`。
 */
function OutcomeRow({
  name,
  badgeText,
  primaryColor,
  logoUrl,
  playerImage,
  contract,
  showScore,
  score,
  onContractClick,
}: Readonly<OutcomeRowProps>) {
  const chance = contract?.chance
  const multiplier = formatOddsMultiplier(chance)
  const chancePct = chance ? Number(chance) : null

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) max-content',
        columnGap: 12,
        alignItems: 'center',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <CompetitorLogo
          size={LOGO_SIZE}
          radius={LOGO_RADIUS}
          primaryColor={primaryColor}
          logoUrl={logoUrl}
          playerImage={playerImage}
          fallbackText={badgeText}
        />
        <Stack gap={6} style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}>
          <Text
            variant="body"
            c="var(--content-primary)"
            truncate
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Text>
          <WinChanceBar color={primaryColor} chancePct={chancePct} />
        </Stack>
      </Box>

      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 12,
          flexShrink: 0,
        }}
      >
        {showScore ? (
          // 比分模式（卡片级）：LiveScoreBox 在无分时自隐藏，该侧留空而不是
          // 回退到倍数——两行保持对称。
          <LiveScoreBox score={score} />
        ) : (
          <Text
            variant="subtitle-emphasis"
            c="var(--content-secondary)"
            style={{ whiteSpace: 'nowrap' }}
          >
            {multiplier}
          </Text>
        )}
        <Box style={{ position: 'relative', zIndex: 2 }}>
          <PriceCell market="moneyline" contract={contract} onContractClick={onContractClick} />
        </Box>
      </Box>
    </div>
  )
}
